"use client";

import React, { useState } from "react";
import { Trophy, Martini, TrainFront, MapPin, Info, Navigation, Map as MapIcon } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
type LocationData = {
  id: string;
  name: string;
  type: string;
  icon: React.ElementType;
  x: string;
  y: string;
  image: string;
  proTip: string;
  hotspots: string[];
};
const LOCATIONS: LocationData[] = [{
  id: "central-precinct",
  name: "Central Precinct HQ",
  type: "Main Station",
  icon: Trophy,
  x: "48%",
  y: "52%",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689786/img/metropolitan_police_hq.png",
  proTip: "The main administrative office is open 24/7 for emergency reports. For records requests, please visit between 8 AM and 5 PM on weekdays.",
  hotspots: ["City Hall", "Municipal Court", "Public Records Office"]
}, {
  id: "north-division",
  name: "North Division Station",
  type: "Substation",
  icon: Martini,
  x: "62%",
  y: "28%",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689786/img/north_division_precinct.png",
  proTip: "Located near the Midtown district. This station handles all community outreach programs for the northern metropolitan area.",
  hotspots: ["Midtown Community Center", "Northside Park", "Division Training Center"]
}, {
  id: "transit-police",
  name: "Transit Police Hub",
  type: "Specialized Unit",
  icon: TrainFront,
  x: "50%",
  y: "68%",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689786/img/transit_police_hub.png",
  proTip: "Officers are stationed here to monitor all major rail and bus intersections. Use the emergency call boxes for immediate assistance.",
  hotspots: ["Central Station", "Metro Transit Authority", "Union Square"]
}, {
  id: "training-academy",
  name: "Police Training Academy",
  type: "Education",
  icon: MapPin,
  x: "32%",
  y: "42%",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689786/img/police_academy_campus.png",
  proTip: "Public tours are available on the first Saturday of every month. Registration is required via the Transparency portal.",
  hotspots: ["Tactical Range", "Recruitment Office", "K9 Training Field"]
}];
export default function PrecinctLocations() {
  const [selectedLocation, setSelectedLocation] = useState<LocationData | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const handleMarkerClick = (location: LocationData) => {
    setSelectedLocation(location);
    setIsSheetOpen(true);
  };
  return <section data-section-id="224884" id="precinct-locations" className="bg-background text-foreground relative w-full overflow-hidden py-24 px-4 md:px-8">
      <div className="container mx-auto max-w-7xl">
        <div className="mb-12 max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-primary/10 text-primary border border-primary/20 mb-6">
            <MapIcon className="size-4" />
            <span className="text-sm font-default font-bold tracking-widest uppercase">Jurisdiction Map</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-default font-bold mb-6 tracking-tight text-primary">
            Precinct Locations
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed font-default">
            Locate your nearest Metropolitan Central Police station. Click on any precinct marker to view contact information, operational hours, and nearby municipal services.
          </p>
        </div>

        {}
        <Card className="relative w-full h-[600px] md:h-[700px] overflow-hidden rounded-md border-border bg-muted shadow-lg">
          {}
          <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689786/img/metropolitan_city_map_blueprint.png)] bg-cover bg-center opacity-40 mix-blend-multiply" style={{
          filter: "grayscale(1) contrast(1.1)"
        }} />
          
          {}
          <div className="absolute inset-0 bg-gradient-to-t from-background/40 via-transparent to-background/10 pointer-events-none" />

          {}
          {LOCATIONS.map((loc, index) => {
          const Icon = loc.icon;
          return <div key={loc.id} data-index={index} className="absolute -translate-x-1/2 -translate-y-1/2 group z-10" style={{
            left: loc.x,
            top: loc.y
          }}>
                {}
                <div className="absolute inset-0 bg-secondary rounded-md animate-ping opacity-20 group-hover:opacity-40 transition-opacity duration-500" />
                
                {}
                <Button variant="outline" size="icon" className="relative rounded-md border-2 border-primary bg-card text-primary hover:bg-primary hover:text-primary-foreground shadow-md w-12 h-12 transition-all duration-300 hover:scale-105" onClick={() => handleMarkerClick(loc)} aria-label={`View details for ${loc.name}`}>
                  <Icon className="size-5" />
                </Button>
                
                {}
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 px-3 py-1.5 bg-primary text-primary-foreground text-xs font-default font-bold rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none whitespace-nowrap border border-primary/50 shadow-lg">
                  {loc.name}
                </div>
              </div>;
        })}
        </Card>
      </div>

      {}
      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <SheetContent side="right" className="bg-card backdrop-blur-xl border-l border-border sm:max-w-md w-full p-0 overflow-y-auto shadow-2xl">
          <SheetHeader className="sr-only">
            <SheetTitle>{selectedLocation?.name}</SheetTitle>
            <SheetDescription>Operational details for {selectedLocation?.name}</SheetDescription>
          </SheetHeader>

          {selectedLocation && <div className="flex flex-col min-h-full">
              {}
              <div className="relative h-72 w-full shrink-0">
                <img src={selectedLocation.image} className="absolute inset-0 w-full h-full object-cover grayscale-[0.5]" alt={selectedLocation.name} />
                <div className="absolute inset-0 bg-gradient-to-t from-card via-card/20 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <span className="px-2.5 py-1 bg-secondary text-secondary-foreground text-xs font-default font-bold uppercase tracking-widest rounded-md border border-secondary/30 mb-3 inline-block">
                    {selectedLocation.type}
                  </span>
                  <h3 className="text-3xl font-default font-bold text-primary leading-tight">
                    {selectedLocation.name}
                  </h3>
                </div>
              </div>

              {}
              <div className="p-6 space-y-8 flex-1">
                {}
                <div className="space-y-3">
                  <h4 className="text-lg font-default font-bold text-primary flex items-center gap-2 uppercase tracking-tight">
                    <Info className="size-5 text-secondary" />
                    Station Information
                  </h4>
                  <p className="text-muted-foreground text-base leading-relaxed font-default">
                    {selectedLocation.proTip}
                  </p>
                </div>

                <div className="h-px w-full bg-border" />

                {}
                <div className="space-y-4">
                  <h4 className="text-lg font-default font-bold text-primary flex items-center gap-2 uppercase tracking-tight">
                    <Navigation className="size-5 text-secondary" />
                    Nearby Facilities
                  </h4>
                  <ul className="space-y-3">
                    {selectedLocation.hotspots.map((spot, i) => <li key={i} data-index={i} className="flex items-center gap-3 p-4 rounded-md bg-muted border border-border hover:border-secondary/50 transition-colors group">
                        <div className="bg-card p-2 rounded-md group-hover:bg-secondary/10 transition-colors">
                          <MapPin className="size-4 text-primary group-hover:text-secondary transition-colors" />
                        </div>
                        <span className="text-sm font-default font-semibold text-foreground">{spot}</span>
                      </li>)}
                  </ul>
                </div>
              </div>
              
              {}
              <div className="p-6 mt-auto border-t border-border bg-muted/50">
                 <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-md font-default font-bold" size="lg">
                    Get Directions
                 </Button>
              </div>
            </div>}
        </SheetContent>
      </Sheet>
    </section>;
}